import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  useTheme,
  Menu,
  MenuItem,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
} from '@mui/material';
import {
  Person,
  ExitToApp,
  Menu as MenuIcon,
  Home,
  Login as LoginIcon,
  PersonAdd,
  AdminPanelSettings,
  Settings,
  Storage,
  Business,
  HelpOutline,
  Close,
} from '@mui/icons-material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import logoImage from '../../assets/setsync_logo_transparent.png';

const Navbar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAuthenticated = !!localStorage.getItem('token');
  const isAdmin = user?.isAdmin;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  // State for admin menu
  const [adminMenuAnchor, setAdminMenuAnchor] = useState(null);
  // State for scroll detection
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    setDrawerOpen(false);
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleAdminMenuOpen = (event) => {
    setAdminMenuAnchor(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchor(null);
  };

  const handleLogoClick = (e) => {
    if (isAuthenticated) {
      e.preventDefault();
      navigate('/');
    }
  };

  const isActive = (path) => location.pathname === path;

  const mobileDrawer = (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        backgroundColor: '#0A0A0A',
        display: 'flex',
        flexDirection: 'column',
      }}
      role="presentation"
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid #1E1E1E',
      }}>
        <img 
          src={logoImage} 
          alt="SetSync Logo" 
          style={{ height: '32px' }} 
        />
        <IconButton onClick={toggleDrawer(false)} sx={{ color: '#FFFFFF' }}>
          <Close />
        </IconButton>
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, py: 3 }}>
        <ListItem 
          component={Link} 
          to="/" 
          onClick={toggleDrawer(false)}
          sx={{ 
            textDecoration: 'none', 
            color: isActive('/') ? '#FF5C00' : '#FFFFFF',
            py: 2,
            '&:hover': { backgroundColor: 'rgba(255, 92, 0, 0.08)' },
          }}
        >
          <ListItemText 
            primary="Home" 
            primaryTypographyProps={{ 
              fontSize: '1.25rem', 
              fontWeight: 500 
            }} 
          />
        </ListItem>

        {/* Marketing Pages - Always Visible */}
        <ListItem 
          component={Link} 
          to="/venues" 
          onClick={toggleDrawer(false)}
          sx={{ 
            textDecoration: 'none', 
            color: isActive('/venues') ? '#FF5C00' : '#FFFFFF',
            py: 2,
            '&:hover': { backgroundColor: 'rgba(255, 92, 0, 0.08)' },
          }}
        >
          <ListItemText 
            primary="For Venues" 
            primaryTypographyProps={{ 
              fontSize: '1.25rem', 
              fontWeight: 500 
            }} 
          />
        </ListItem>
        <ListItem 
          component={Link} 
          to="/djs" 
          onClick={toggleDrawer(false)}
          sx={{ 
            textDecoration: 'none', 
            color: isActive('/djs') ? '#FF5C00' : '#FFFFFF',
            py: 2,
            '&:hover': { backgroundColor: 'rgba(255, 92, 0, 0.08)' },
          }}
        >
          <ListItemText 
            primary="For DJs" 
            primaryTypographyProps={{ 
              fontSize: '1.25rem', 
              fontWeight: 500 
            }} 
          />
        </ListItem>
        {/* Pricing link hidden during founding phase */}
        {/* About link hidden during founding phase */}

        <Divider sx={{ my: 2, borderColor: '#1E1E1E' }} />

        {isAuthenticated ? (
          <>
            <ListItem 
              component={Link} 
              to={user.userType === 'dj' ? '/dj/dashboard' : '/venue/dashboard'}
              onClick={toggleDrawer(false)}
              sx={{ 
                textDecoration: 'none', 
                color: '#FFFFFF',
                py: 2,
                '&:hover': { backgroundColor: 'rgba(255, 92, 0, 0.08)' },
              }}
            >
              <ListItemText 
                primary="Dashboard" 
                primaryTypographyProps={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 500 
                }} 
              />
            </ListItem>

            {user.userType === 'venue' && (
              <ListItem 
                component={Link} 
                to="/venue/support/retailplayer"
                onClick={toggleDrawer(false)}
                sx={{ 
                  textDecoration: 'none', 
                  color: '#FFFFFF',
                  py: 2,
                  '&:hover': { backgroundColor: 'rgba(255, 92, 0, 0.08)' },
                }}
              >
                <ListItemText 
                  primary="Support" 
                  primaryTypographyProps={{ 
                    fontSize: '1.25rem', 
                    fontWeight: 500 
                  }} 
                />
              </ListItem>
            )}

            {isAdmin && (
              <>
                <Divider sx={{ my: 2, borderColor: '#1E1E1E' }} />
                <Typography 
                  sx={{ 
                    px: 2, 
                    py: 1, 
                    color: '#666666', 
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                  }}
                >
                  Admin
                </Typography>
                <ListItem 
                  component={Link} 
                  to="/admin/servers"
                  onClick={toggleDrawer(false)}
                  sx={{ 
                    textDecoration: 'none', 
                    color: '#FFFFFF',
                    py: 1.5,
                    '&:hover': { backgroundColor: 'rgba(255, 92, 0, 0.08)' },
                  }}
                >
                  <ListItemIcon sx={{ color: '#888888' }}><Storage /></ListItemIcon>
                  <ListItemText primary="Manage Servers" />
                </ListItem>
                <ListItem 
                  component={Link} 
                  to="/admin/butt-config"
                  onClick={toggleDrawer(false)}
                  sx={{ 
                    textDecoration: 'none', 
                    color: '#FFFFFF',
                    py: 1.5,
                    '&:hover': { backgroundColor: 'rgba(255, 92, 0, 0.08)' },
                  }}
                >
                  <ListItemIcon sx={{ color: '#888888' }}><Settings /></ListItemIcon>
                  <ListItemText primary="BUTT Configuration" />
                </ListItem>
                <ListItem 
                  component={Link} 
                  to="/admin/venues"
                  onClick={toggleDrawer(false)}
                  sx={{ 
                    textDecoration: 'none', 
                    color: '#FFFFFF',
                    py: 1.5,
                    '&:hover': { backgroundColor: 'rgba(255, 92, 0, 0.08)' },
                  }}
                >
                  <ListItemIcon sx={{ color: '#888888' }}><Business /></ListItemIcon>
                  <ListItemText primary="Manage Venues" />
                </ListItem>
              </>
            )}
          </>
        ) : (
          <>
            <ListItem 
              component={Link} 
              to="/login"
              onClick={toggleDrawer(false)}
              sx={{ 
                textDecoration: 'none', 
                color: '#FFFFFF',
                py: 2,
                '&:hover': { backgroundColor: 'rgba(255, 92, 0, 0.08)' },
              }}
            >
              <ListItemText 
                primary="Login" 
                primaryTypographyProps={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 500 
                }} 
              />
            </ListItem>
          </>
        )}
      </List>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #1E1E1E' }}>
        {isAuthenticated ? (
          <Box>
            <Typography sx={{ color: '#666666', fontSize: '0.875rem', mb: 1 }}>
              Signed in as {user.name}
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleLogout}
              startIcon={<ExitToApp />}
              sx={{
                borderColor: '#333333',
                color: '#FFFFFF',
                '&:hover': {
                  borderColor: '#FF5C00',
                  backgroundColor: 'rgba(255, 92, 0, 0.08)',
                },
              }}
            >
              Logout
            </Button>
          </Box>
        ) : (
          <Button
            fullWidth
            variant="contained"
            component={Link}
            to="/register"
            onClick={toggleDrawer(false)}
          >
            Get Started
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <AppBar 
      position="fixed"
      sx={{
        backgroundColor: scrolled ? alpha('#0A0A0A', 0.9) : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid #1E1E1E' : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <Toolbar sx={{ 
        maxWidth: 'lg', 
        width: '100%', 
        mx: 'auto',
        py: 1,
      }}>
        {/* Logo */}
        <Box 
          component={Link} 
          to="/"
          onClick={handleLogoClick}
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            textDecoration: 'none',
            mr: 4,
          }}
        >
          <img 
            src={logoImage} 
            alt="SetSync Logo" 
            style={{ 
              height: isMobile ? '32px' : '40px',
            }} 
          />
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Marketing Links - Always Visible */}
            <Button
              component={Link}
              to="/venues"
              sx={{
                color: isActive('/venues') ? '#FF5C00' : '#888888',
                fontWeight: 500,
                fontSize: '0.85rem',
                '&:hover': {
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Venues
            </Button>
            <Button
              component={Link}
              to="/djs"
              sx={{
                color: isActive('/djs') ? '#FF5C00' : '#888888',
                fontWeight: 500,
                fontSize: '0.85rem',
                '&:hover': {
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              DJs
            </Button>
            {/* Pricing and About links hidden during founding phase */}
            {/* <Button
              component={Link}
              to="/about"
              sx={{
                color: isActive('/about') ? '#FF5C00' : '#888888',
                fontWeight: 500,
                fontSize: '0.85rem',
                '&:hover': {
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              About
            </Button> */}

            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: '#333333' }} />

            {isAuthenticated ? (
              <>
                {/* Dashboard Link */}
                <Button
                  component={Link}
                  to={user.userType === 'dj' ? '/dj/dashboard' : '/venue/dashboard'}
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  Dashboard
                </Button>

                {/* Support for Venues */}
                {user.userType === 'venue' && (
                  <Button
                    component={Link}
                    to="/venue/support/retailplayer"
                    sx={{
                      color: '#888888',
                      fontWeight: 500,
                      '&:hover': {
                        color: '#FFFFFF',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    Support
                  </Button>
                )}

                {/* Admin Menu */}
                {isAdmin && (
                  <>
                    <IconButton
                      onClick={handleAdminMenuOpen}
                      sx={{ 
                        color: '#888888',
                        '&:hover': { color: '#FF5C00' },
                      }}
                    >
                      <AdminPanelSettings />
                    </IconButton>
                    <Menu
                      anchorEl={adminMenuAnchor}
                      open={Boolean(adminMenuAnchor)}
                      onClose={handleAdminMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem 
                        component={Link} 
                        to="/admin/servers" 
                        onClick={handleAdminMenuClose}
                      >
                        <ListItemIcon><Storage fontSize="small" /></ListItemIcon>
                        <ListItemText>Manage Servers</ListItemText>
                      </MenuItem>
                      <MenuItem 
                        component={Link} 
                        to="/admin/butt-config" 
                        onClick={handleAdminMenuClose}
                      >
                        <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                        <ListItemText>BUTT Configuration</ListItemText>
                      </MenuItem>
                      <MenuItem 
                        component={Link} 
                        to="/admin/venues" 
                        onClick={handleAdminMenuClose}
                      >
                        <ListItemIcon><Business fontSize="small" /></ListItemIcon>
                        <ListItemText>Manage Venues</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}

                {/* User Info & Logout */}
                <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: '#333333' }} />
                <Typography sx={{ color: '#888888', fontSize: '0.875rem' }}>
                  {user.name}
                </Typography>
                <IconButton
                  onClick={handleLogout}
                  sx={{ 
                    color: '#888888',
                    '&:hover': { color: '#FF5C00' },
                  }}
                >
                  <ExitToApp />
                </IconButton>
              </>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/login"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  component={Link}
                  to="/register"
                  sx={{ ml: 1 }}
                >
                  Get Started
                </Button>
              </>
            )}
          </Box>
        )}

        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>
      
      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: '100vw',
          },
        }}
      >
        {mobileDrawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
